(function () {
  const dataEl = document.getElementById('gtm-page-data');
  if (!dataEl) return;

  /** @type {any} */
  let pageData;
  try {
    pageData = JSON.parse(dataEl.textContent || '');
  } catch {
    return;
  }

  const { pageType, currency } = pageData;

  // Cast window once to avoid repeated type suppressions
  const win = /** @type {any} */ (window);
  win.dataLayer = win.dataLayer || [];

  // Clear previous ecommerce object before each push, per GA4 spec
  /** @param {Record<string, unknown>} obj */
  function push(obj) {
    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push(obj);
  }

  // Products already sent in a view_item_list this page view, by product id
  // (read by the Algolia takeover listener below).
  /** @type {Set<string>} */
  const listedProducts = new Set();

  // Build items array from product card data attributes (collection + search pages)
  /**
   * @param {string} listName
   * @param {string} listId
   * @param {boolean} [onlyNew] - skip cards whose product was already listed this page view
   * @returns {any[]}
   */
  function getCardItems(listName, listId, onlyNew) {
    /** @type {any[]} */
    const items = [];
    document.querySelectorAll('product-card[data-gtm-item]').forEach((card, index) => {
      try {
        const el = /** @type {HTMLElement} */ (card);
        const productId = el.dataset.productId || '';
        if (onlyNew && (!productId || listedProducts.has(productId))) return;
        const item = JSON.parse(el.dataset.gtmItem || '');
        items.push({ ...item, index: index + 1, item_list_name: listName, item_list_id: listId });
        if (productId) listedProducts.add(productId);
      } catch {}
    });
    return items;
  }

  // view_item
  if (pageType === 'product' && pageData.product) {
    push({
      event: 'view_item',
      ecommerce: { currency, value: pageData.product.price, items: [pageData.product] },
    });
  }

  // view_cart
  if (pageType === 'cart' && pageData.items) {
    push({
      event: 'view_cart',
      ecommerce: { currency, value: pageData.value, items: pageData.items },
    });
  }

  // Send a view_item_list for the cards on the page (collection + search).
  /** @param {boolean} [onlyNew] - see getCardItems */
  function listCards(onlyNew) {
    const listName = pageData.listName || '';
    const listId = String(pageData.listId || '');
    const items = getCardItems(listName, listId, onlyNew);
    if (items.length) {
      push({
        event: 'view_item_list',
        ecommerce: { currency, item_list_name: listName, item_list_id: listId, items },
      });
    }
  }

  // An Algolia takeover still to paint (@localised/search; the listeners are
  // further down). Its grid holds Shopify's server-rendered cards, masked,
  // which the first render replaces (or, on a collection that keeps Shopify's
  // page 1, confirms). Listed now they would name products the shopper never
  // sees, and mark them listed, so the products Algolia shows in their place
  // would be dropped or numbered wrong. While one is pending, the list waits
  // for the first render, or for a stand-down to make the native cards final.
  //
  // What holds when this deferred file runs: the settings section's inline
  // config script ran during parsing, and the grid host (a takeover section's
  // own <ul>, emitted only with its toggle on) is in the parsed DOM; both are
  // needed for a takeover. The bundle's first render takes two network round
  // trips, but the browser can complete them while the module scripts ahead of
  // this file load, so one that has already happened is read from what it
  // left: the nativeFacets.rendered / .broken flags localised-native-facets.liquid
  // sets, the Algolia-rendered <li>s (data-handle) in the host, and
  // html.ls-scope-broken.
  // Then the cards on the page are the ones shown and are listed now.
  const ls = win.LocalisedSearch || {};
  const lsHost = document.querySelector('[data-ls-grid-host]');
  const lsFacets = ls.nativeFacets || {};
  let awaitingTakeover =
    (pageType === 'collection' || pageType === 'search') &&
    !!lsHost &&
    !!ls.config &&
    (ls.config.pageType === 'collection' || ls.config.pageType === 'search') &&
    !lsFacets.rendered &&
    !lsFacets.broken &&
    !document.documentElement.classList.contains('ls-scope-broken') &&
    !lsHost.querySelector('li[data-handle]');

  // A stand-down can also land before this file runs, with its native
  // re-render still in flight (nativeFacets.restoring, see the stand-down
  // listeners below): the cards on the page are about to be replaced, so the
  // list waits for localised:native:restored here too.
  let awaitingNative =
    (pageType === 'collection' || pageType === 'search') && !awaitingTakeover && !!lsFacets.restoring;

  // view_item_list (collection + search) — with no takeover pending, exactly
  // as without Algolia: every card on the page, once, at load.
  if ((pageType === 'collection' || pageType === 'search') && !awaitingTakeover && !awaitingNative) {
    listCards();
  }

  // search
  if (pageType === 'search' && pageData.searchQuery) {
    push({ event: 'search', search_term: pageData.searchQuery });
  }

  // select_item — click on a product card link
  document.addEventListener('click', function (e) {
    const target = /** @type {Element | null} */ (e.target);
    if (!target) return;
    const card = /** @type {HTMLElement | null} */ (target.closest('product-card[data-gtm-item]'));
    if (!card) return;
    if (!target.closest('a')) return;

    let item;
    try {
      item = JSON.parse(card.dataset.gtmItem || '');
    } catch {
      return;
    }

    const listName = pageData.listName || '';
    const listId = String(pageData.listId || '');
    push({ event: 'select_item', ecommerce: { currency, item_list_name: listName, item_list_id: listId, items: [item] } });
  });

  // add_to_cart — hooks into CartAddEvent which bubbles as 'cart:update'
  //
  // Lookup order:
  //   1. variantMap (PDP) — keyed by variantId, includes exact variant title/price
  //   2. productCardMap (collection quick-add) — keyed by productId from product-card elements.
  //      The original product-card stays in the DOM even after quick-add moves the form into
  //      its modal, so this lookup works regardless of where the form is rendered. Re-read
  //      whenever the Algolia takeover swaps cards in (see below).
  const variantMap = /** @type {Record<string, any>} */ (pageData.variants || {});

  /** @type {Record<string, any>} */
  const productCardMap = {};
  function mapCards() {
    document.querySelectorAll('product-card[data-product-id][data-gtm-item]').forEach((card) => {
      try {
        const el = /** @type {HTMLElement} */ (card);
        productCardMap[el.dataset.productId || ''] = JSON.parse(el.dataset.gtmItem || '');
      } catch {}
    });
  }
  mapCards();

  document.addEventListener('cart:update', function (e) {
    const ce = /** @type {CustomEvent} */ (e);
    if (!ce.detail?.data) return;
    const { source, didError, itemCount, productId } = ce.detail.data;
    if (source !== 'product-form-component' || didError) return;

    const variantId = /** @type {string} */ (ce.detail.sourceId);
    const quantity = /** @type {number} */ (itemCount || 1);

    // Prefer exact variant data from PDP variantMap; fall back to card default for quick-add.
    // Only override item_id when using variantMap — the data there is exact for the added variant.
    // For card-based lookups the card's item_id, item_variant, and price are consistent with
    // each other (all describe the card's default variant) so we leave them untouched.
    const fromVariantMap = variantId in variantMap;
    const item = (fromVariantMap ? variantMap[variantId] : null) || productCardMap[productId];
    if (!item) return;

    push({
      event: 'add_to_cart',
      ecommerce: {
        currency,
        value: /** @type {number} */ (item.price) * quantity,
        items: [{ ...item, ...(fromVariantMap ? { item_id: variantId } : {}), quantity }],
      },
    });
  });

  // Algolia takeover (@localised/search on collection + search pages) replaces
  // the grid's cards after load: each Algolia render swaps in freshly rendered
  // product-cards, a chunk at a time with one localised:hits:rendered per
  // chunk, and a colour swatch can swap a single card (localised:card:swapped).
  // Both lookups above were read from the server render, so refresh the
  // quick-add map, and list the cards Algolia shows that this page view has not
  // reported yet — a product is listed at most once, in the order and at the
  // position it is displayed. The first render also ends the load-time wait
  // (awaitingTakeover above): nothing was listed from the masked grid, so its
  // cards are listed in full. A "keep Shopify's page 1" settle fires the same
  // event (detail.native) over the cards already shown, which lists them.
  // Neither event fires with the takeover off or after it stands down: native
  // behaviour is unchanged.
  document.addEventListener('localised:hits:rendered', function () {
    mapCards();
    awaitingTakeover = false;
    if (pageType !== 'collection' && pageType !== 'search') return;
    listCards(true);
  });
  document.addEventListener('localised:card:swapped', mapCards);

  // The takeover handed back before it painted (the scope gate's
  // localised:scope:broken, or the no-render failsafe's
  // localised:takeover:standdown in localised-native-facets.liquid): the
  // grid's cards are Shopify's for the rest of the page view, so they are the
  // list, sent once. Ignored unless the load-time list is still waiting.
  //
  // Both stand-downs start a native re-render of the section, and its
  // response can hold other products than the masked grid (a filter chosen
  // during the wait, a synthetic price bound dropped, an empty grid that
  // fills). So the list is taken from the grid that render leaves: the
  // bridge marks it in flight (nativeFacets.restoring) and fires
  // localised:native:restored when it settles. If it failed, the cards still
  // on the page are the ones shown, and those are listed. The check runs a
  // task later, once the bridge's own stand-down handlers have started the
  // render, whichever order the listeners were registered in. With no render
  // in flight (no bridge on the page) the cards on the page are final now.
  function listNative() {
    awaitingNative = false;
    mapCards();
    listCards(true);
  }
  function listNativeAfterStandDown() {
    if (!awaitingTakeover) return;
    awaitingTakeover = false;
    awaitingNative = true;
    setTimeout(function () {
      if (!awaitingNative) return;
      const facets = (win.LocalisedSearch && win.LocalisedSearch.nativeFacets) || {};
      if (facets.restoring) return;
      listNative();
    }, 0);
  }
  document.addEventListener('localised:scope:broken', listNativeAfterStandDown);
  document.addEventListener('localised:takeover:standdown', listNativeAfterStandDown);
  document.addEventListener('localised:native:restored', function () {
    if (awaitingNative) listNative();
  });

  // sign_up — Klaviyo forms dispatch a 'klaviyoForms' event on successful submission
  document.addEventListener('klaviyoForms', function (e) {
    const ce = /** @type {CustomEvent} */ (e);
    if (ce.detail?.type !== 'submit') return;
    push({ event: 'sign_up', method: 'email' });
  });
})();
